import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Card, CardBody, Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { buildNestedObject } from "../../helpers";
import { 
    RiBankCard2Line, 
    RiCalendarCheckFill, 
    RiFileChart2Line, 
    RiFileTextLine, 
    RiInformationLine, 
    RiLineChartLine, 
    RiMoneyDollarCircleLine 
} from "react-icons/ri";
import { KeysTitles } from "../../constants";
import FinancialTable from "./components/financial-table";
import LocalFinancialTable from "./components/local-financial-table";

const Financial = ({
    t,
    activeAccordion,
    setActiveAccordion,

    detailsQuery
}) => {
    if (detailsQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const generic = buildNestedObject(organizationData?.additional_information?.generic || []);

    const financial_statements = organizationData?.financial_statements || [];

    const local_financial_statements = organizationData?.local_financial_statements || [];

    const payment_data = buildNestedObject(organizationData?.payment_data?.generic || []);

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('financial')}
            className={activeAccordion === 'financial' ? 'active' : ''}
            eventKey={'financial'}
        >
            <Accordion.Header>
                <RiMoneyDollarCircleLine 
                    size="24" 
                    className="me-2" 
                />

                <span>
                    {t('Financial Information')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                {/* Financial Statements Table */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiFileChart2Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Financial Statements')}
                            </div>
                        </div>

                        <FinancialTable
                            data={financial_statements}
                        />
                    </CardBody>
                </Card>

                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiFileTextLine size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Local Financial Statements')}
                            </div>
                        </div>

                        <LocalFinancialTable 
                            data={local_financial_statements}
                        />
                    </CardBody>
                </Card>

                {/* payment data */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiBankCard2Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Payment Data')}
                            </div>
                        </div>

                        {/* Simple fields */}
                        <Row>
                            {['dbt', 'industryDbt', 'paymentTrend', 'averageMonthlyBalance', 'balance', 'balanceP1', 'balanceP2', 'balanceP3', 'balanceP4', 'balanceWithinTerms', 'recentHighBalance', 'totalTradeLines', 'totalTradeLinesOutstanding', 'invoicesTotal', 'invoicesUpTo30Days', 'invoicesMoreThan30Days', 'invoicesOutstanding', 'invoicesBeyond', 'totalInvoiceValue', 'averageInvoiceValue', 'totalBalanceStillOwing', 'highestInvoiceValueOutstanding', 'totalBalanceStillOwingLate', 'highestInvoiceValueOutstandingLate'].map(key => (
                                <Col className="mb-2" sm="6" md="4" key={key}>
                                    <strong>
                                        {t(KeysTitles[key]?.title || key)}
                                        {KeysTitles[key]?.description ? (
                                            <>
                                                {' '}
                                                <span
                                                    id={`tooltip-financials-payments-${key}`}
                                                    style={{ cursor: "pointer" }}>
                                                    <RiInformationLine />
                                                </span>
                                                <UncontrolledTooltip
                                                    target={`tooltip-financials-payments-${key}`}
                                                >
                                                    {t(KeysTitles[key].description)}
                                                </UncontrolledTooltip>
                                            </>
                                        ) : null}
                                    </strong>{': '}
                                    
                                    {payment_data[key]}
                                </Col>
                            ))}
                        </Row>

                        <hr />

                        {/* Nested objects as tables */}
                        {payment_data.invoicesBetween3And12Monts && (
                            <div className="mb-4">
                                <strong>{t('Invoices Between 3 and 12 Months')}</strong>
                                <Table striped bordered className="mb-2">
                                    <thead>
                                        <tr>
                                            {Object.keys(payment_data.invoicesBetween3And12Monts).map(k => (
                                                <th key={k}>
                                                    {t(KeysTitles[k]?.title || k)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            {Object.values(payment_data.invoicesBetween3And12Monts).map((v, i) => <td key={i}>{v}</td>)}
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {payment_data.invoicesLast3Monts && (
                            <div className="mb-4">
                                <strong>{t('Invoices Last 3 Months')}</strong>
                                <Table striped bordered className="mb-2">
                                    <thead>
                                        <tr>
                                            {Object.keys(payment_data.invoicesLast3Monts).map(k => <th key={k}>
                                                {t(KeysTitles[k]?.title || k)}
                                            </th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            {Object.values(payment_data.invoicesLast3Monts).map((v, i) => <td key={i}>{v}</td>)}
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Arrays with month data */}
                        {payment_data.annualDbtWithTrend && (
                            <div className="mb-4">
                                <strong>{t('Annual DBT With Trend')}</strong>
                                <Table striped bordered className="mb-2">
                                    <thead>
                                        <tr>
                                            <th>{t('Month')}</th>
                                            <th>{t('DBT Value')}</th>
                                            <th>{t('DBT Trend')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payment_data.annualDbtWithTrend.map((entry, i) => (
                                            <tr key={i}>
                                                <td>{entry.month}</td>
                                                <td>{entry.dbtValue}</td>
                                                <td>{entry.dbtTrend}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {payment_data.lastSixMonthsBalance && (
                            <div className="mb-4">
                                <strong>{t('Last Six Months Balance')}</strong>
                                <Table striped bordered>
                                    <thead>
                                        <tr>
                                            <th>{t('Month')}</th>
                                            <th>{t('Balance')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payment_data.lastSixMonthsBalance.map((entry, i) => (
                                            <tr key={i}>
                                                <td>{entry.month}</td>
                                                <td>{entry.balance}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Payment Expectations Summary */}
                {generic?.paymentExpectationsSummary && (
                    <Card className="h-100 h-100 shadow-sm mb-4">
                        <CardBody className="p-1">
                            <div className="d-flex align-items-center mb-2">
                                <span className="me-2 text-primary">
                                    <RiCalendarCheckFill size="24" />
                                </span>

                                <div className="fw-bold font-size-16">
                                    {t('Payment Expectations Summary')}
                                </div>
                            </div>

                            <Row>
                                {Object.entries(generic.paymentExpectationsSummary).map(
                                    ([key, value]) => (
                                        <Col sm="6" key={key} className="mb-2">
                                            <strong>
                                                {t(KeysTitles[key]?.title || key)}:{' '}
                                            </strong> 
                                            {value}
                                        </Col>
                                    )
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Enquiries Trend */}
                {generic?.enquiriesTrend && (
                    <Card className="h-100 h-100 shadow-sm mb-4">
                        <CardBody className="p-1">
                            <div className="d-flex align-items-center mb-2">
                                <span className="me-2 text-primary">
                                    <RiLineChartLine size="24" />
                                </span>

                                <div className="fw-bold font-size-16">
                                    {t('Enquiries Trend')}
                                </div>
                            </div>

                            <Row>
                                {Object.entries(generic.enquiriesTrend).map(
                                    ([key, value]) => (
                                        <Col sm="6" md="4" key={key} className="mb-2">
                                            <strong>
                                                {t(KeysTitles[key]?.title || key)}:
                                            </strong> {value}
                                        </Col>
                                    )
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                )}
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    Financial
)